import React, { useState, useRef, useEffect } from 'react';
import { SmilePlus } from 'lucide-react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';

export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '😡', '🙏', '🎉', '🔥', '👏', '💯', '✅', '😍', '🤔', '👎', '🤝'];

export function ReactionControl({ msgId, reactions = {}, currentUserId, mine, onChanged, disabled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const myReaction = reactions?.[currentUserId] || '';
  const agg = {};
  Object.values(reactions || {}).forEach((e) => { agg[e] = (agg[e] || 0) + 1; });

  const react = async (emoji) => {
    if (busy || disabled || !msgId || String(msgId).startsWith('tmp-')) return;
    setBusy(true);
    setOpen(false);
    try {
      const res = await apiCall(`/api/hub/messages/${msgId}/react`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      });
      onChanged?.(msgId, res.reactions || {});
    } catch (e) {
      toast.error('Could not react', { description: e.message });
    }
    setBusy(false);
  };

  const pillBase = mine ? 'bg-white/15 border-white/25 text-white' : 'bg-white border-slate-200 text-slate-700';

  return (
    <div className="relative flex flex-wrap items-center gap-1 mt-1" ref={ref} data-testid={`reactions-${msgId}`}>
      {Object.entries(agg).map(([emoji, count]) => (
        <button
          key={emoji}
          type="button"
          onClick={() => react(emoji)}
          className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px] leading-none transition ${pillBase} ${
            myReaction === emoji ? (mine ? 'ring-1 ring-white/70' : 'ring-1 ring-indigo-400 bg-indigo-50') : ''
          }`}
          title={myReaction === emoji ? 'Remove your reaction' : `React with ${emoji}`}
          data-testid={`reaction-pill-${emoji}`}
        >
          <span>{emoji}</span>
          <span className="font-semibold">{count}</span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className={`rounded-full border p-0.5 opacity-60 hover:opacity-100 transition ${pillBase}`}
        title="Add reaction"
        data-testid={`react-btn-${msgId}`}
      >
        <SmilePlus className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className={`absolute bottom-full mb-1 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-2 grid grid-cols-8 gap-1 w-max ${mine ? 'right-0' : 'left-0'}`}
          data-testid="reaction-picker"
        >
          {REACTION_EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => react(e)}
              className={`text-lg leading-none p-1 rounded-lg hover:bg-slate-100 transition ${myReaction === e ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''}`}
              data-testid={`emoji-option-${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
